import React, { Suspense } from 'react';
import { ErrorBoundary } from '@funtech-int/rival-react';

import {
    MoviePrerollViewContainer,
    MoviePrerollViewContainerProps,
} from '../../movie/movie-preroll-view/container';
import { useSkippableFragments } from '../../../../film-player/hooks/use-skippable-fragments';
import { useFilmCurrentStream } from '../../../../film-player/hooks/use-film-current-stream';
import { getNotNullableItems } from '../../../../../lib/get-not-nullable-items';

import { useFilmFavoriteToggleWithAnalytics } from './hooks/use-film-favorite-toggle-with-analytics';

interface FilmPrerollContainer
    extends Omit<
        MoviePrerollViewContainerProps,
        'skippableFragments' | 'movieId' | 'prerollContent' | 'isPlannedToWatch' | 'onBookmarkClick'
    > {
    contentId: string;
}

interface FilmPrerollViewProps extends FilmPrerollContainer {
    prerollTargetMovieId: number;
}

const FilmPrerollView = ({ contentId, prerollTargetMovieId, ...props }: FilmPrerollViewProps) => {
    const skippableFragments = useSkippableFragments(contentId);

    const { onFilmFavoriteToggleWithAnalylics, isPlannedToWatch, promotedContentId } =
        useFilmFavoriteToggleWithAnalytics({
            contentId,
            promotedMovieid: prerollTargetMovieId,
        });

    if (!promotedContentId) {
        return null;
    }

    return (
        <MoviePrerollViewContainer
            skippableFragments={skippableFragments}
            movieId={prerollTargetMovieId}
            isPlannedToWatch={Boolean(isPlannedToWatch)}
            onBookmarkClick={onFilmFavoriteToggleWithAnalylics}
            {...props}
        />
    );
};

export const FilmPrerollViewContainer = ({ contentId, ...props }: FilmPrerollContainer) => {
    const stream = useFilmCurrentStream(contentId);

    const prerolls = getNotNullableItems(stream?.streamMeta?.prerolls);
    const prerollTargetMovieId = prerolls[0]?.promotedEntityId || 0;

    if (!prerollTargetMovieId) {
        return null;
    }

    return (
        <ErrorBoundary fallback={() => null}>
            <Suspense fallback={null}>
                <FilmPrerollView
                    contentId={contentId}
                    prerollTargetMovieId={prerollTargetMovieId}
                    {...props}
                />
            </Suspense>
        </ErrorBoundary>
    );
};
